import type { V2 } from '../../core/math';
import type { Decor } from '../decor';
import { pointInPolygon } from '../geom';
import type { CityLayout } from '../types';
import { ChunkedBuilders, hash01, type RGB } from './meshBuilder';
import { BAKED_GROUND, T, tileUV } from './textures';

/**
 * Ground plane of the district: asphalt carriageways, raised pavements with granite kerbs, yards,
 * gardens, granite setts, road markings, quay walls and the water itself.
 *
 * Height ladder (m): road 0.00 · setts 0.01 · markings 0.015 · pavement 0.12 · yards 0.125 ·
 * grass 0.13 · paths 0.135 · water -1.4.
 */
export const PAVE_Y = 0.12;
export const WATER_Y = -1.4;

// With the baked set the kerb's granite texture carries the colour (materials.ts, world-space uv) and the vertex
// colour is a tone on top of it; the painter has no kerb texture, so there the vertex colour is the granite.
const KERB_C: RGB = BAKED_GROUND ? [1, 1, 1] : [0.3, 0.29, 0.28];
const KERB_TOP_C: RGB = BAKED_GROUND ? [1.1, 1.09, 1.07] : [0.34, 0.333, 0.323];
const KERB_STONE = BAKED_GROUND ? ([0.95, 0.3] as const) : ([0.3, 0.09] as const);
const WHITE_PAINT: RGB = [0.82, 0.8, 0.74];
const YELLOW_PAINT: RGB = [0.86, 0.66, 0.16];

export function buildGround(layout: CityLayout, decor: Decor, cb: ChunkedBuilders): void {
  const { nodes, edges } = layout.roads;
  const EXT = 760;

  // ---------------------------------------------------------------- base asphalt (roads)
  cb.global('asphalt').polygon(
    [
      { x: -EXT, z: -EXT },
      { x: EXT, z: -EXT },
      { x: EXT, z: EXT },
      { x: -EXT, z: EXT },
    ],
    0,
    [1, 1, 1],
  );

  // ---------------------------------------------------------------- setts overlays
  const halfAt = (nodeId: number, except: number): number => {
    let h = 0;
    for (const e of edges) {
      if (e.id === except || (e.from !== nodeId && e.to !== nodeId)) continue;
      h = Math.max(h, e.width / 2);
    }
    return h;
  };
  for (const e of edges) {
    if (!decor.setts[e.id]) continue;
    const a = nodes[e.from]!.pos;
    const b = nodes[e.to]!.pos;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const d = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    const l = { x: d.z, z: -d.x };
    const t0 = nodes[e.from]!.intersectionId !== null ? halfAt(e.from, e.id) : 0;
    const t1 = len - (nodes[e.to]!.intersectionId !== null ? halfAt(e.to, e.id) : 0);
    const hw = e.width / 2 + 0.05;
    const P = (t: number, s: number): V2 => ({ x: a.x + d.x * t + l.x * s, z: a.z + d.z * t + l.z * s });
    cb.global('setts').polygon([P(t0, -hw), P(t1, -hw), P(t1, hw), P(t0, hw)], 0.01, [0.95, 0.95, 0.95]);
  }

  // ---------------------------------------------------------------- pavements, kerbs, yards
  for (const f of decor.faces) {
    if (f.outer) {
      const rect = [
        { x: -EXT, z: -EXT },
        { x: EXT, z: -EXT },
        { x: EXT, z: EXT },
        { x: -EXT, z: EXT },
      ];
      cb.global('paving').polygon(rect, PAVE_Y, [1, 1, 1], 1, [f.kerb]);
      cb.global('yard').polygon(rect, PAVE_Y + 0.005, [0.95, 0.95, 0.95], 1, [f.line]);
    } else {
      cb.global('paving').polygon(f.kerb, PAVE_Y, [1, 1, 1]);
      const quay = f.district === 'gullet-basin' || f.line.some((p) => p.z > 130 && p.z < 195);
      cb.global(quay ? 'setts' : 'yard').polygon(f.line, PAVE_Y + 0.005, quay ? [0.85, 0.85, 0.85] : [0.95, 0.95, 0.95]);
    }
    kerbStrip(cb, f.kerb, f.outer);
  }

  // grass and park paths
  for (const g of decor.grass) cb.global('grass').polygon(g, PAVE_Y + 0.01, [1, 1, 1]);
  for (const p of decor.parkPaths) {
    const d = { x: p.b.x - p.a.x, z: p.b.z - p.a.z };
    const len = Math.hypot(d.x, d.z);
    const n = { x: (-d.z / len) * (p.width / 2), z: (d.x / len) * (p.width / 2) };
    cb.global('gravel').polygon(
      [
        { x: p.a.x + n.x, z: p.a.z + n.z },
        { x: p.b.x + n.x, z: p.b.z + n.z },
        { x: p.b.x - n.x, z: p.b.z - n.z },
        { x: p.a.x - n.x, z: p.a.z - n.z },
      ],
      PAVE_Y + 0.015,
      [1, 1, 1],
    );
  }
  // ring path round the bandstand
  {
    const ring: V2[] = [];
    const hole: V2[] = [];
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      ring.push({ x: 150 + Math.cos(a) * 9, z: 30 + Math.sin(a) * 9 });
      hole.push({ x: 150 + Math.cos(a) * 6.2, z: 30 + Math.sin(a) * 6.2 });
    }
    cb.global('gravel').polygon(ring, PAVE_Y + 0.016, [1, 1, 1], 1, [hole]);
  }

  // ---------------------------------------------------------------- markings
  const mk = cb.global('markings');
  const Y = 0.016;
  /**
   * A painted line. Road paint wears: every strip is a little duller or brighter than the last, so a
   * dashed centre line is not a row of identical bars, and the lines near junctions (where wheels
   * cross them) are the most worn.
   */
  const strip = (a: V2, b: V2, off: number, width: number, c: RGB, wear = 1): void => {
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len < 0.05) return;
    const d = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    const l = { x: d.z, z: -d.x };
    const s0 = off - width / 2;
    const s1 = off + width / 2;
    const h = hash01(Math.round(a.x * 3), Math.round(a.z * 3), Math.round(off * 10));
    // (the baked set's wear mask dulls the paint further, so its base is higher)
    const f = (BAKED_GROUND ? 0.72 + h * 0.33 : 0.58 + h * 0.42) * wear;
    const cw: RGB = [c[0] * f, c[1] * f, c[2] * f];
    mk.polygon(
      [
        { x: a.x + l.x * s0, z: a.z + l.z * s0 },
        { x: b.x + l.x * s0, z: b.z + l.z * s0 },
        { x: b.x + l.x * s1, z: b.z + l.z * s1 },
        { x: a.x + l.x * s1, z: a.z + l.z * s1 },
      ],
      Y,
      cw,
    );
  };
  const radiusAt = (nodeId: number): number => {
    const it = nodes[nodeId]!.intersectionId;
    return it !== null ? layout.intersections[it]!.radius : 0;
  };
  const rank = (w: number): number => w;
  for (const e of edges) {
    if (e.alley) continue;
    const a = nodes[e.from]!.pos;
    const b = nodes[e.to]!.pos;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const d = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    const at = (t: number): V2 => ({ x: a.x + d.x * t, z: a.z + d.z * t });
    const r0 = radiusAt(e.from);
    const r1 = radiusAt(e.to);
    const bridge = decor.bridges.some((br) => br.edgeId === e.id);
    // centre line (two-way only)
    if (!e.oneWay) {
      const start = r0 + 1.5;
      const end = len - r1 - 1.5;
      const dash = bridge ? 6 : 3;
      const gap = bridge ? 2 : 5;
      if (bridge) strip(at(start), at(end), 0, 0.12, WHITE_PAINT);
      else for (let t = start; t + dash <= end; t += dash + gap) strip(at(t), at(t + dash), 0, 0.12, WHITE_PAINT);
    }
    // stop / give-way lines at each junction approach (left half of the road for traffic arriving)
    for (const [nodeId, into] of [
      [e.to, true],
      [e.from, false],
    ] as const) {
      const it = nodes[nodeId]!.intersectionId;
      if (it === null) continue;
      if (e.oneWay && !into) continue;
      const inter = layout.intersections[it]!;
      const t = into ? len - inter.radius : inter.radius;
      const p = at(t);
      // lanes for traffic heading INTO the node are on the left of the travel direction
      const travel = into ? d : { x: -d.x, z: -d.z };
      const q = { x: p.x + travel.x * 0.01, z: p.z + travel.z * 0.01 };
      const half = e.width / 2;
      const lo = e.oneWay ? -half : 0;
      const major = Math.max(...inter.edgeIds.map((id) => rank(edges[id]!.width)));
      const signalled = !!inter.signal;
      const across = (s0: number, s1: number, width: number): void => {
        // a strip perpendicular to travel, from lateral offset s0 to s1 (left positive)
        const l = { x: travel.z, z: -travel.x };
        const c0 = { x: p.x + l.x * s0, z: p.z + l.z * s0 };
        const c1 = { x: p.x + l.x * s1, z: p.z + l.z * s1 };
        strip(c0, c1, 0, width, WHITE_PAINT);
      };
      void q;
      if (signalled) across(lo + 0.2, half - 0.2, 0.35);
      else if (rank(e.width) < major - 0.01 || e.oneWay) {
        // give way: double dashed line
        for (let s = lo + 0.3; s < half - 0.4; s += 0.9) {
          const l = { x: travel.z, z: -travel.x };
          for (const back of [0, 0.5]) {
            const c0 = { x: p.x - travel.x * back + l.x * s, z: p.z - travel.z * back + l.z * s };
            const c1 = { x: c0.x + l.x * 0.55, z: c0.z + l.z * 0.55 };
            strip(c0, c1, 0, 0.2, WHITE_PAINT);
          }
        }
      }
    }
    // double yellow lines near junctions (no waiting), both kerbs
    if (!bridge && e.lanesForward + e.lanesBackward > 0) {
      for (const side of [1, -1]) {
        const off = side * (e.width / 2 - 0.3);
        const segs: [number, number][] = [
          [r0, Math.min(len / 2, r0 + 12)],
          [Math.max(len / 2, len - r1 - 12), len - r1],
        ];
        for (const [t0, t1] of segs) {
          if (t1 - t0 < 1) continue;
          strip(at(t0), at(t1), off, 0.08, YELLOW_PAINT);
          strip(at(t0), at(t1), off - side * 0.18, 0.08, YELLOW_PAINT);
        }
      }
    }
  }
  // zebra crossings: the stripes are worn through in the wheel tracks, and zig-zags lead up to them
  for (const z of decor.zebras) {
    const d = { x: Math.sin(z.heading), z: Math.cos(z.heading) };
    const l = { x: d.z, z: -d.x };
    const half = z.width / 2 - 0.2;
    for (let s = -half; s < half - 0.3; s += 1.0) {
      const c0 = { x: z.pos.x + l.x * s - d.x * 1.5, z: z.pos.z + l.z * s - d.z * 1.5 };
      const c1 = { x: z.pos.x + l.x * s + d.x * 1.5, z: z.pos.z + l.z * s + d.z * 1.5 };
      const inTrack = Math.abs(Math.abs(s) - z.width * 0.25) < 0.9;
      strip(c0, c1, 0.25, 0.5, WHITE_PAINT, inTrack ? 0.7 : 1);
    }
    for (const side of [1, -1]) {
      for (let k = 0; k < 6; k++) {
        const t0 = 2.2 + k * 1.6;
        const o = (k % 2 ? 0.55 : -0.55) * side;
        for (const sgn of [1, -1]) {
          const a = { x: z.pos.x + d.x * sgn * t0 + l.x * (side * (z.width / 2 - 0.5) + o), z: z.pos.z + d.z * sgn * t0 + l.z * (side * (z.width / 2 - 0.5) + o) };
          const b = { x: z.pos.x + d.x * sgn * (t0 + 1.6) + l.x * (side * (z.width / 2 - 0.5) - o), z: z.pos.z + d.z * sgn * (t0 + 1.6) + l.z * (side * (z.width / 2 - 0.5) - o) };
          strip(a, b, 0, 0.1, WHITE_PAINT, 0.9);
        }
      }
    }
  }

  // ---------------------------------------------------------------- water + quay walls
  const water = cb.global('water');
  for (const w of decor.water) {
    water.polygon(
      [
        { x: Math.max(w.minX, -EXT), z: Math.max(w.minZ, -EXT) },
        { x: Math.min(w.maxX, EXT), z: Math.max(w.minZ, -EXT) },
        { x: Math.min(w.maxX, EXT), z: Math.min(w.maxZ, EXT) },
        { x: Math.max(w.minX, -EXT), z: Math.min(w.maxZ, EXT) },
      ],
      WATER_Y,
      [1, 1, 1],
    );
  }
  const quayWall = (x0: number, z0: number, x1: number, z1: number): void => {
    const len = Math.hypot(x1 - x0, z1 - z0);
    const n = Math.max(1, Math.round(len / 4));
    for (let i = 0; i < n; i++) {
      const ax = x0 + ((x1 - x0) * i) / n;
      const az = z0 + ((z1 - z0) * i) / n;
      const bx = x0 + ((x1 - x0) * (i + 1)) / n;
      const bz = z0 + ((z1 - z0) * (i + 1)) / n;
      const b = cb.get('facade', (ax + bx) / 2, (az + bz) / 2);
      // darker towards the water (baked contact shading, docs/POLISH.md "Graphics batch 2")
      b.quad([ax, WATER_Y - 0.6, az], [bx, WATER_Y - 0.6, bz], [bx, PAVE_Y, bz], [ax, PAVE_Y, az], tileUV(T.stonePlain), [0.47, 0.455, 0.425], [0.62, 0.6, 0.56]);
      // green algae band at the waterline
      b.quad([ax, WATER_Y - 0.1, az], [bx, WATER_Y - 0.1, bz], [bx, WATER_Y + 0.5, bz], [ax, WATER_Y + 0.5, az], tileUV(T.white, 40), [0.12, 0.16, 0.1]);
    }
  };
  const C = decor.water[0]!;
  const B = decor.water[1]!;
  // Face normal = direction x up, so walk -x -> +x for a wall facing +z (south) into the water.
  quayWall(-EXT, C.minZ, EXT, C.minZ);
  quayWall(EXT, C.maxZ, -EXT, C.maxZ);
  quayWall(B.minX, B.minZ, EXT, B.minZ);
  quayWall(B.minX, EXT, B.minX, B.minZ);
}

/** Granite kerb: vertical face from road level to pavement level, plus a lighter top edge. */
function kerbStrip(cb: ChunkedBuilders, poly: V2[], outer: boolean): void {
  const kb = cb.global('kerb');
  const lite = cb.global('kerbLite');
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len < 0.01) continue;
    const d = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
    let nrm = { x: d.z, z: -d.x };
    const mid = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
    const inside = pointInPolygon({ x: mid.x + nrm.x * 0.2, z: mid.z + nrm.z * 0.2 }, poly);
    // nrm should point towards the road (away from the pavement)
    const pavementSide = outer ? !inside : inside;
    if (pavementSide) nrm = { x: -nrm.x, z: -nrm.z };
    const face = (p: V2, y: number): [number, number, number] => [p.x, y, p.z];
    // vertical face, outward normal towards road
    const p0 = face(a, -0.02);
    const p1 = face(b, -0.02);
    const p2 = face(b, PAVE_Y + 0.01);
    const p3 = face(a, PAVE_Y + 0.01);
    kb.quadFacing(p0, p1, p2, p3, [0, 0, 1, 1], KERB_C, [nrm.x, 0, nrm.z]);
    // MEDIUM / LOW draw `kerbLite` instead of `kerb` (cityView, by quality tier): the same face and a
    // plain top strip in place of the stones.
    const ta = { x: a.x - nrm.x * 0.28, z: a.z - nrm.z * 0.28 };
    const tb = { x: b.x - nrm.x * 0.28, z: b.z - nrm.z * 0.28 };
    lite.quadFacing(p0, p1, p2, p3, [0, 0, 1, 1], KERB_C, [nrm.x, 0, nrm.z]);
    lite.quadFacing(face(a, PAVE_Y + 0.011), face(b, PAVE_Y + 0.011), face(tb, PAVE_Y + 0.011), face(ta, PAVE_Y + 0.011), [0, 0, 1, 1], KERB_TOP_C, [0, 1, 0]);
    // top edge, laid as individual granite kerb stones (~0.9 m) with slightly different tones
    const stones = Math.max(1, Math.round(len / 0.9));
    for (let k = 0; k < stones; k++) {
      const t0 = k / stones;
      const t1 = (k + 1) / stones - Math.min(0.02 / len, 0.2 / stones);
      const sa = { x: a.x + (b.x - a.x) * t0, z: a.z + (b.z - a.z) * t0 };
      const sb = { x: a.x + (b.x - a.x) * t1, z: a.z + (b.z - a.z) * t1 };
      const ia = { x: sa.x - nrm.x * 0.28, z: sa.z - nrm.z * 0.28 };
      const ib = { x: sb.x - nrm.x * 0.28, z: sb.z - nrm.z * 0.28 };
      const h = hash01(Math.round(sa.x * 7), Math.round(sa.z * 7), 5);
      const tone = KERB_STONE[0] + h * KERB_STONE[1];
      kb.quadFacing(face(sa, PAVE_Y + 0.011), face(sb, PAVE_Y + 0.011), face(ib, PAVE_Y + 0.011), face(ia, PAVE_Y + 0.011), [0, 0, 1, 1], [tone, tone * 0.98, tone * 0.95], [0, 1, 0]);
    }
  }
}

/** Lamp pools and wet-road light streaks (additive decals). */
export function buildLampPools(decor: Decor, cb: ChunkedBuilders): void {
  const b = cb.global('pools');
  const Y = PAVE_Y + 0.03;
  const quadAt = (cx: number, cz: number, hx: number, hz: number, c: RGB): void => {
    b.quad([cx - hx, Y, cz + hz], [cx + hx, Y, cz + hz], [cx + hx, Y, cz - hz], [cx - hx, Y, cz - hz], [0, 0, 1, 1], c);
  };
  for (const p of decor.props) {
    // Street and bridge lamps are breakable and drawn by lampsView; only wall lamps are baked here.
    if (p.type !== 'wallLamp') continue;
    const fx = Math.sin(p.heading);
    const fz = Math.cos(p.heading);
    const reach = p.type === 'wallLamp' ? 0.8 : 1.3;
    const hx = p.pos.x + fx * reach;
    const hz = p.pos.z + fz * reach;
    const sodium: RGB = p.type === 'wallLamp' ? [0.3, 0.18, 0.07] : [0.46, 0.24, 0.08];
    const size = p.type === 'wallLamp' ? 2.8 : 4.4;
    quadAt(hx, hz, size, size, sodium);
    // wet reflection streak towards the camera (+z on screen)
    if (p.type !== 'wallLamp') {
      quadAt(hx, hz + 4.2, 0.9, 4.6, [sodium[0] * 0.55, sodium[1] * 0.55, sodium[2] * 0.55]);
      // Reflection on the water between the lamp and the viewer (camera looks north).
      for (const w of decor.water) {
        const sz = hz + 7;
        if (hx > w.minX && hx < w.maxX && sz > w.minZ + 1 && sz < w.maxZ) {
          const z0 = Math.max(w.minZ + 0.3, hz + 1);
          b.quad([hx - 0.9, WATER_Y + 0.02, z0 + 11], [hx + 0.9, WATER_Y + 0.02, z0 + 11], [hx + 0.9, WATER_Y + 0.02, z0], [hx - 0.9, WATER_Y + 0.02, z0], [0, 0, 1, 1], [0.55, 0.3, 0.1]);
          break;
        }
      }
    }
  }
}
